/**
 * @module features/users/components/ViewUserModal
 * @description Responsive Drawer/Sheet for viewing complete user profile data,
 * configurable FIDO2 passkey metrics, 2FA status, and audit timestamps.
 * On mobile, opens as an edge-to-edge bottom sheet. On desktop/tablet, slides out from the right.
 */

import * as React from "react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/badge";
import { UserAvatar } from "./UserAvatar";
import { UserDto } from "@/types/auth";
import {
  Shield,
  Fingerprint,
  KeyRound,
  Mail,
  Phone,
  Clock,
  Copy,
  Check,
  Edit,
  ShieldOff,
} from "lucide-react";

interface ViewUserModalProps {
  user: UserDto | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (user: UserDto) => void;
  onDisableTotp?: (user: UserDto) => void;
}

export function ViewUserModal({
  user,
  open,
  onClose,
  onEdit,
  onDisableTotp,
}: ViewUserModalProps) {
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  if (!user) return null;

  const handleCopy = (text: string, field: string) => {
    try {
      navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Ignore clipboard failure in restricted browser context
    }
  };

  const passkeyLimit = user.max_passkeys || 5;
  const passkeyPercent = Math.min(
    100,
    Math.round((user.passkey_count / passkeyLimit) * 100)
  );

  const formatDate = (isoString?: string | null) => {
    if (!isoString) return "Never";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return isoString;
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="User Details"
      description="Account permissions, passkey device capacity, and audit history."
    >
      <div className="space-y-4">
        {/* User Profile Header Card */}
        <div className="flex items-center justify-between gap-3 p-3.5 rounded-xl border border-border/80 dark:border-zinc-800 bg-muted/30 dark:bg-zinc-900/40">
          <div className="flex items-center gap-3 min-w-0">
            <UserAvatar
              name={user.name}
              avatarUrl={user.avatar_url}
              size="lg"
            />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground truncate">
                {user.name}
              </h3>
              <p className="text-xs font-mono text-muted-foreground truncate">
                {user.email_id}
              </p>
            </div>
          </div>

          <StatusBadge
            status={user.role === "super_admin" ? "active" : "neutral"}
            label={user.role === "super_admin" ? "Super Admin" : "Client"}
            className="shrink-0 text-xs font-mono"
          />
        </div>

        {/* User ID & Online Status Row */}
        <div className="flex items-center justify-between p-2.5 rounded-xl border border-border/70 dark:border-zinc-800/80 bg-card text-xs">
          <div className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground min-w-0 flex-1">
            <span className="font-semibold text-foreground shrink-0">ID:</span>
            <span className="font-mono tracking-tight text-foreground select-all break-all sm:break-normal">
              {user.id}
            </span>
            <button
              type="button"
              onClick={() => handleCopy(user.id, "id")}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors shrink-0"
              title="Copy User ID"
            >
              {copiedField === "id" ? (
                <Check className="h-3.5 w-3.5 text-foreground" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 pl-2">
            <span
              className={`h-2 w-2 rounded-full ${
                user.is_logged_in ? "bg-emerald-500" : "bg-muted-foreground/40"
              }`}
            />
            <span className="text-[11px] text-muted-foreground font-medium">
              {user.is_logged_in ? "Online" : "Offline"}
            </span>
          </div>
        </div>

        {/* Security & Credentials */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-foreground" />
            <span>Security & Credentials</span>
          </h4>

          <div className="grid grid-cols-2 gap-2.5">
            {/* Passkeys Device Capacity */}
            <div className="p-3 rounded-xl border border-border/80 dark:border-zinc-800 bg-card space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Fingerprint className="h-3.5 w-3.5 text-foreground" />
                  Passkeys
                </span>
                <span className="font-mono font-semibold text-foreground">
                  {user.passkey_count} / {passkeyLimit}
                </span>
              </div>
              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-zinc-950 dark:bg-zinc-100 rounded-full transition-all duration-300"
                  style={{ width: `${passkeyPercent}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground">
                Limit: {passkeyLimit} max devices
              </p>
            </div>

            {/* TOTP 2FA Status */}
            <div className="p-3 rounded-xl border border-border/80 dark:border-zinc-800 bg-card space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  <KeyRound className="h-3.5 w-3.5 text-purple-500" />
                  2FA TOTP
                </span>
                <span
                  className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${
                    user.is_totp_enabled
                      ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {user.is_totp_enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground pt-1">
                {user.is_totp_enabled
                  ? "Authenticator active"
                  : "Not configured yet"}
              </p>
              {user.is_totp_enabled && onDisableTotp && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onDisableTotp(user)}
                  className="w-full mt-2 h-7 text-[11px] text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive"
                >
                  <ShieldOff className="h-3 w-3 mr-1" />
                  Disable 2FA
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Contact Information
          </h4>

          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between p-2.5 rounded-xl border border-border/80 dark:border-zinc-800 bg-card">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-3.5 w-3.5" />
                <span>Email:</span>
              </div>
              <div className="flex items-center gap-1.5 font-mono text-foreground font-medium">
                <span>{user.email_id}</span>
                <button
                  type="button"
                  onClick={() => handleCopy(user.email_id, "email")}
                  className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                  title="Copy Email"
                >
                  {copiedField === "email" ? (
                    <Check className="h-3.5 w-3.5 text-foreground" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between p-2.5 rounded-xl border border-border/80 dark:border-zinc-800 bg-card">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />
                <span>Phone:</span>
              </div>
              <span className="font-mono text-foreground font-medium">
                {user.contact_number
                  ? `${user.country_code} ${user.contact_number}`
                  : "Not provided"}
              </span>
            </div>
          </div>
        </div>

        {/* Audit Timestamps */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span>Audit History</span>
          </h4>

          <div className="rounded-xl border border-border/80 dark:border-zinc-800 bg-card divide-y divide-border/60 dark:divide-zinc-800 text-[11px] font-mono">
            <div className="flex items-center justify-between p-2.5">
              <span className="text-muted-foreground">Created:</span>
              <span className="text-foreground">{formatDate(user.created_at)}</span>
            </div>
            <div className="flex items-center justify-between p-2.5">
              <span className="text-muted-foreground">Last Updated:</span>
              <span className="text-foreground">{formatDate(user.updated_at)}</span>
            </div>
            <div className="flex items-center justify-between p-2.5">
              <span className="text-muted-foreground">Last Login:</span>
              <span className="text-foreground">{formatDate(user.last_login)}</span>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="pt-3 flex items-center justify-between gap-2 border-t border-border mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="h-9 px-4 text-xs"
          >
            Close
          </Button>
          {onEdit && (
            <Button
              type="button"
              onClick={() => {
                onClose();
                onEdit(user);
              }}
              className="h-9 px-4 text-xs gap-1.5 shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
            >
              <Edit className="h-3.5 w-3.5" />
              <span>Edit User</span>
            </Button>
          )}
        </div>
      </div>
    </Drawer>
  );
}
